/**
 * Permissions Utility
 * Provides helper functions for checking user permissions and roles
 */

import { normalizeRole, isAdminRole } from './roleUtils';

/**
 * Check if user is admin
 * @param {Object} currentUser - Current user object
 * @returns {boolean}
 */
export const isAdmin = (currentUser) => {
  if (!currentUser) return false;
  return isAdminRole(currentUser.role);
};

/**
 * Check if user is teacher
 * @param {Object} currentUser - Current user object
 * @returns {boolean}
 */
export const isTeacher = (currentUser) => {
  return currentUser?.role === 'teacher';
};

/**
 * Check if user is student
 * @param {Object} currentUser - Current user object
 * @returns {boolean}
 */
export const isStudent = (currentUser) => {
  return currentUser?.role === 'student';
};

/**
 * Check if user is parent
 * @param {Object} currentUser - Current user object
 * @returns {boolean}
 */
export const isParent = (currentUser) => {
  return currentUser?.role === 'parent';
};

/**
 * Check if user is staff
 * @param {Object} currentUser - Current user object
 * @returns {boolean}
 */
export const isStaff = (currentUser) => {
  return currentUser?.role === 'staff';
};

/**
 * Check if user can create resources
 * Admin always can, others depend on specific conditions
 * @param {Object} currentUser - Current user object
 * @param {Function} additionalCheck - Additional condition function
 * @returns {boolean}
 */
export const canCreate = (currentUser, additionalCheck = null) => {
  if (isAdmin(currentUser)) return true;
  if (additionalCheck) return additionalCheck(currentUser);
  return false;
};

/**
 * Check if user can edit resources
 * Admin always can, others depend on ownership or specific conditions
 * @param {Object} currentUser - Current user object
 * @param {Object} resource - Resource to check ownership
 * @param {Function} additionalCheck - Additional condition function
 * @returns {boolean}
 */
export const canEdit = (currentUser, resource = null, additionalCheck = null) => {
  if (isAdmin(currentUser)) return true;
  
  // Check ownership if resource provided
  if (resource && resource.user_id === currentUser?.id) return true;
  
  // Check additional conditions
  if (additionalCheck) return additionalCheck(currentUser, resource);
  
  return false;
};

/**
 * Check if user can delete resources
 * Admin always can, others depend on ownership or specific conditions
 * @param {Object} currentUser - Current user object
 * @param {Object} resource - Resource to check ownership
 * @param {Function} additionalCheck - Additional condition function
 * @returns {boolean}
 */
export const canDelete = (currentUser, resource = null, additionalCheck = null) => {
  if (isAdmin(currentUser)) return true;
  
  // Check ownership if resource provided
  if (resource && resource.user_id === currentUser?.id) return true;
  
  // Check additional conditions
  if (additionalCheck) return additionalCheck(currentUser, resource);
  
  return false;
};

/**
 * Check if user can view resources
 * Admin can view all, others depend on specific conditions
 * @param {Object} currentUser - Current user object
 * @param {Function} additionalCheck - Additional condition function
 * @returns {boolean}
 */
export const canView = (currentUser, additionalCheck = null) => {
  if (isAdmin(currentUser)) return true;
  if (additionalCheck) return additionalCheck(currentUser);
  return true; // Default to view access
};

/**
 * Check if user has any of the specified roles
 * @param {Object} currentUser - Current user object
 * @param {Array<string>} roles - Array of roles to check
 * @returns {boolean}
 */
export const hasRole = (currentUser, roles = []) => {
  if (!currentUser || !roles.length) return false;
  return roles.includes(currentUser.role);
};

/**
 * Check if user can manage users
 * Only admin can manage users
 * @param {Object} currentUser - Current user object
 * @returns {boolean}
 */
export const canManageUsers = (currentUser) => {
  return isAdmin(currentUser);
};

/**
 * Check if user can manage classes
 * Admin and teachers can manage classes
 * @param {Object} currentUser - Current user object
 * @returns {boolean}
 */
export const canManageClasses = (currentUser) => {
  return isAdmin(currentUser) || isTeacher(currentUser);
};

/**
 * Check if user can manage grades
 * Admin and teachers can manage grades
 * @param {Object} currentUser - Current user object
 * @returns {boolean}
 */
export const canManageGrades = (currentUser) => {
  return isAdmin(currentUser) || isTeacher(currentUser);
};

/**
 * Check if user can manage attendance
 * Admin and teachers can manage attendance
 * @param {Object} currentUser - Current user object
 * @returns {boolean}
 */
export const canManageAttendance = (currentUser) => {
  return isAdmin(currentUser) || isTeacher(currentUser);
};

/**
 * Get user permissions object
 * Convenience function to get all permissions at once
 * @param {Object} currentUser - Current user object
 * @returns {Object} Permissions object
 */
export const getUserPermissions = (currentUser) => {
  return {
    isAdmin: isAdmin(currentUser),
    isTeacher: isTeacher(currentUser),
    isStudent: isStudent(currentUser),
    isParent: isParent(currentUser),
    isStaff: isStaff(currentUser),
    canManageUsers: canManageUsers(currentUser),
    canManageClasses: canManageClasses(currentUser),
    canManageGrades: canManageGrades(currentUser),
    canManageAttendance: canManageAttendance(currentUser),
    canCreate: (additionalCheck) => canCreate(currentUser, additionalCheck),
    canEdit: (resource, additionalCheck) => canEdit(currentUser, resource, additionalCheck),
    canDelete: (resource, additionalCheck) => canDelete(currentUser, resource, additionalCheck),
    canView: (additionalCheck) => canView(currentUser, additionalCheck)
  };
};

